import Color from 'color';
import type uPlot from 'uplot';

import {type Line} from '../../../util/plotHelpers/types';

/**
 * Mutable draw state for the stacked-area hook, read at draw time so hovering
 * never rebuilds the uPlot options (which would recreate the chart).
 */
export type StackedAreaDrawState = {
  /**
   * When set (the highlight overlay layer), only these lines are painted — the
   * hovered run's area, drawn bright on top of the dimmed base. `undefined` (the
   * main layer) paints every provided line.
   */
  highlightedLines?: ReadonlySet<Line>;
};

/**
 * Stacked-area and percent-area lines: non-aux lines whose `type` is 'area'.
 * `convertLinesToArea` produces these — each point carries a baseline (`y0`) and
 * a top (`y`) that are already cumulatively stacked (and, for percent area,
 * normalized). Aux `area` lines are min/max confidence bands and are excluded;
 * those still render through uPlot's native band machinery.
 */
export function getStackedAreaLines(lines: Line[]): Line[] {
  return lines.filter(line => line.aux !== true && line.type === 'area');
}

/**
 * Draw filled stacked / percent areas directly to the uPlot canvas — the
 * d3.area equivalent that react-vis's `AreaSeriesCanvas` uses.
 *
 * Each run is filled as a polygon: its top edge (`x`, `y`) left→right, then its
 * baseline (`x`, `y0`) right→left. Filling the polygon per point (rather than
 * with a uPlot `band`) is what makes this correct when the two edges cross —
 * which they do wherever a stacked value is negative, or for percent area — a
 * case uPlot's band model cannot render.
 *
 * Lines are drawn in array order; `convertLinesToArea` stacks bottom-first, so
 * later runs layer on top, matching react-vis's paint order.
 */
export function createDrawStackedAreasHook(
  lines: Line[],
  getState?: () => StackedAreaDrawState
): (u: uPlot) => void {
  return (u: uPlot) => {
    // On the overlay layer getState returns the hovered run(s); paint only
    // those, bright, on top. On the main layer there is no getState, so paint
    // all lines.
    const highlightedLines = getState?.().highlightedLines;
    const linesToDraw =
      highlightedLines != null
        ? lines.filter(line => highlightedLines.has(line))
        : lines;
    if (linesToDraw.length === 0) {
      return;
    }
    const ctx = u.ctx;
    const {left, top, width, height} = u.bbox;

    ctx.save();
    // Clip to the plot area so an area near the edge can't paint into the axis
    // margins.
    ctx.beginPath();
    ctx.rect(left, top, width, height);
    ctx.clip();

    for (const line of linesToDraw) {
      const points = line.data;
      if (points.length === 0) {
        continue;
      }

      ctx.beginPath();
      // Top edge (y), left → right.
      for (let i = 0; i < points.length; i++) {
        const px = u.valToPos(points[i].x, 'x', true);
        const py = u.valToPos(points[i].y, 'y', true);
        if (i === 0) {
          ctx.moveTo(px, py);
        } else {
          ctx.lineTo(px, py);
        }
      }
      // Baseline (y0), right → left, closing the polygon.
      for (let i = points.length - 1; i >= 0; i--) {
        const px = u.valToPos(points[i].x, 'x', true);
        const py0 = u.valToPos(points[i].y0 ?? 0, 'y', true);
        ctx.lineTo(px, py0);
      }
      ctx.closePath();

      ctx.fillStyle = Color(line.color ?? 'red')
        .rgb()
        .string();
      ctx.fill();
    }

    ctx.restore();
  };
}
